export type Ft3dPathPoint = { x: number; y: number; z: number };

export type Ft3dElevationTransitionKind = 'none' | 'step' | 'climb';

export type Ft3dElevationTransition = {
  pathFt: Ft3dPathPoint[];
  kind: Ft3dElevationTransitionKind;
  riseFt: number;
  boundaryFt: { x: number; y: number } | null;
  links?: Ft3dElevationLink[];
};

export type Footprint = { minX: number; maxX: number; minY: number; maxY: number; shape?: 'ellipse' };

export type Ft3dSupportSurface = {
  /** Scene placement key used for routing and footprint lookup. */
  id: string;
  /** Persisted inventory UUID sent to PHP; scene IDs are intentionally composite. */
  inventoryId?: string;
  z: number;
  footprint: Footprint;
  /** Server-reconstructable top used whenever this is an intermediate link. */
  authoredZ?: number;
};

export type Ft3dElevationLink = {
  fromZ: number;
  toZ: number;
  boundaryFt: { x: number; y: number };
  kind: Exclude<Ft3dElevationTransitionKind, 'none'>;
};

const EPS = 1e-9;

export function insideFootprint(point: readonly [number, number], footprint: Footprint): boolean {
  if (footprint.shape === 'ellipse') {
    const rx = (footprint.maxX - footprint.minX) / 2, ry = (footprint.maxY - footprint.minY) / 2;
    return rx > EPS && ry > EPS && ((point[0] - footprint.minX - rx) / rx) ** 2
      + ((point[1] - footprint.minY - ry) / ry) ** 2 <= 1 + 1e-6;
  }
  return point[0] >= footprint.minX - 1e-6 && point[0] <= footprint.maxX + 1e-6
    && point[1] >= footprint.minY - 1e-6 && point[1] <= footprint.maxY + 1e-6;
}

function footprintBoundaryOnSegment(
  from: readonly [number, number],
  to: readonly [number, number],
  footprint: Footprint,
): [number, number] | null {
  if (footprint.shape === 'ellipse') {
    const interval = footprintIntervalOnSegment(from, to, footprint);
    if (!interval) return null;
    const t = insideFootprint(from, footprint) ? interval.exit : interval.enter;
    return [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t];
  }
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  let tMin = 0;
  let tMax = 1;
  for (const [p, q] of [
    [-dx, from[0] - footprint.minX],
    [dx, footprint.maxX - from[0]],
    [-dy, from[1] - footprint.minY],
    [dy, footprint.maxY - from[1]],
  ] as Array<[number, number]>) {
    if (Math.abs(p) < EPS) {
      if (q < 0) return null;
      continue;
    }
    const ratio = q / p;
    if (p < 0) tMin = Math.max(tMin, ratio);
    else tMax = Math.min(tMax, ratio);
    if (tMin > tMax) return null;
  }
  const ratio = insideFootprint(from, footprint) ? tMax : tMin;
  return [from[0] + dx * ratio, from[1] + dy * ratio];
}

function footprintIntervalOnSegment(
  from: readonly [number, number],
  to: readonly [number, number],
  footprint: Footprint,
): { enter: number; exit: number } | null {
  if (footprint.shape === 'ellipse') {
    const rx = (footprint.maxX - footprint.minX) / 2, ry = (footprint.maxY - footprint.minY) / 2;
    if (rx <= EPS || ry <= EPS) return null;
    const x = (from[0] - footprint.minX - rx) / rx, y = (from[1] - footprint.minY - ry) / ry;
    const dx = (to[0] - from[0]) / rx, dy = (to[1] - from[1]) / ry;
    const a = dx * dx + dy * dy, b = 2 * (x * dx + y * dy), c = x * x + y * y - 1;
    if (a < EPS) return c <= 0 ? { enter: 0, exit: 1 } : null;
    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return null;
    const enter = Math.max(0, (-b - Math.sqrt(discriminant)) / (2 * a));
    const exit = Math.min(1, (-b + Math.sqrt(discriminant)) / (2 * a));
    return enter <= exit + EPS ? { enter: Math.min(1, enter), exit: Math.max(0, exit) } : null;
  }
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  let enter = 0;
  let exit = 1;
  for (const [p, q] of [
    [-dx, from[0] - footprint.minX],
    [dx, footprint.maxX - from[0]],
    [-dy, from[1] - footprint.minY],
    [dy, footprint.maxY - from[1]],
  ] as Array<[number, number]>) {
    if (Math.abs(p) < EPS) {
      if (q < 0) return null;
      continue;
    }
    const ratio = q / p;
    if (p < 0) enter = Math.max(enter, ratio);
    else exit = Math.min(exit, ratio);
    if (enter > exit) return null;
  }
  return { enter, exit };
}

function insetFootprint(
  footprint: Footprint,
  halfWidthFt: number,
  halfDepthFt: number,
): Footprint | null {
  const inset = {
    minX: footprint.minX + halfWidthFt,
    maxX: footprint.maxX - halfWidthFt,
    minY: footprint.minY + halfDepthFt,
    maxY: footprint.maxY - halfDepthFt,
  };
  return inset.minX <= inset.maxX + 1e-6 && inset.minY <= inset.maxY + 1e-6 ? inset : null;
}

/**
 * Retain the current authored landing across a narrow pointer seam without ever producing an
 * illegal endpoint. A sample inside the deadband is clamped back onto the support's valid
 * creature-centre bounds; once it leaves the bound, normal support resolution takes over.
 */
export function stabilizeFt3dSupportSeamPoint(args: {
  point: { x: number; y: number };
  surface: Ft3dSupportSurface;
  moverWidthFt: number;
  moverDepthFt: number;
  hysteresisFt?: number;
}): { x: number; y: number } | null {
  const bounds = insetFootprint(
    args.surface.footprint,
    Math.max(0, args.moverWidthFt) / 2,
    Math.max(0, args.moverDepthFt) / 2,
  );
  if (!bounds) return null;
  const tolerance = Math.max(0, Math.min(1, args.hysteresisFt ?? 0.5));
  if (args.point.x < bounds.minX - tolerance || args.point.x > bounds.maxX + tolerance
    || args.point.y < bounds.minY - tolerance || args.point.y > bounds.maxY + tolerance) return null;
  return {
    x: Math.max(bounds.minX, Math.min(bounds.maxX, args.point.x)),
    y: Math.max(bounds.minY, Math.min(bounds.maxY, args.point.y)),
  };
}

function footprintCenter(footprint: Footprint): { x: number; y: number } {
  return { x: (footprint.minX + footprint.maxX) / 2, y: (footprint.minY + footprint.maxY) / 2 };
}

function footprintsTouch(left: Footprint, right: Footprint): boolean {
  return left.minX <= right.maxX + 1e-6 && left.maxX >= right.minX - 1e-6
    && left.minY <= right.maxY + 1e-6 && left.maxY >= right.minY - 1e-6;
}

/**
 * Finds the shortest legal authored support sequence. A creature without an admitted climb may
 * still reach a tall landing when adjacent supports form size-scaled steps. Climb-capable actors
 * retain the direct route; origin and destination supports are both retained so a separated
 * platform-to-platform move descends and ascends at their real edges.
 */
export function planFt3dSupportChain(args: {
  originFt: Ft3dPathPoint;
  destinationFt: Ft3dPathPoint;
  baseSupportZ: number;
  originSupportId: string | null;
  destinationSupportId: string | null;
  surfaces: Ft3dSupportSurface[];
  moverWidthFt: number;
  moverDepthFt: number;
  stepUpThresholdFt: number;
  climbAdmitted: boolean;
}): Ft3dSupportSurface[] | null {
  const threshold = Math.max(2.5, args.stepUpThresholdFt);
  const byId = new Map(args.surfaces.map(surface => [surface.id, surface]));
  const originSurface = args.originSupportId ? byId.get(args.originSupportId) ?? null : null;
  const destinationSurface = args.destinationSupportId ? byId.get(args.destinationSupportId) ?? null : null;
  if (args.destinationSupportId && !destinationSurface) return null;
  if (args.originSupportId && !originSurface) return null;
  if (!originSurface && !destinationSurface) return [];
  if (args.climbAdmitted) {
    return [originSurface, destinationSurface]
      .filter((surface, index, values): surface is Ft3dSupportSurface => (
        surface !== null && values.findIndex(candidate => candidate?.id === surface.id) === index
      ));
  }

  const halfWidth = Math.max(0, args.moverWidthFt) / 2;
  const halfDepth = Math.max(0, args.moverDepthFt) / 2;
  const nodes = args.surfaces.flatMap(surface => {
    const centerBounds = insetFootprint(surface.footprint, halfWidth, halfDepth);
    return centerBounds ? [{ surface, centerBounds }] : [];
  });
  const nodeById = new Map(nodes.map((node, index) => [node.surface.id, index]));
  const startIndex = originSurface ? nodeById.get(originSurface.id) : undefined;
  const goalIndex = destinationSurface ? nodeById.get(destinationSurface.id) : undefined;
  if (originSurface && startIndex === undefined) return null;
  if (destinationSurface && goalIndex === undefined) return null;

  const distances = nodes.map(() => Number.POSITIVE_INFINITY);
  const previous = nodes.map(() => -1);
  const visited = nodes.map(() => false);
  if (startIndex !== undefined) {
    distances[startIndex] = 0;
  } else {
    for (let index = 0; index < nodes.length; index++) {
      const node = nodes[index]!;
      if (Math.abs(node.surface.z - args.baseSupportZ) > threshold + 1e-6) continue;
      const center = footprintCenter(node.centerBounds);
      distances[index] = Math.hypot(center.x - args.originFt.x, center.y - args.originFt.y)
        + Math.abs(node.surface.z - args.baseSupportZ);
    }
  }

  for (;;) {
    let current = -1;
    for (let index = 0; index < nodes.length; index++) {
      if (!visited[index] && Number.isFinite(distances[index])
        && (current < 0 || distances[index]! < distances[current]!)) current = index;
    }
    if (current < 0) break;
    visited[current] = true;
    if (goalIndex !== undefined && current === goalIndex) break;
    for (let next = 0; next < nodes.length; next++) {
      if (visited[next] || next === current) continue;
      const from = nodes[current]!;
      const to = nodes[next]!;
      if (!footprintsTouch(from.centerBounds, to.centerBounds)
        || Math.abs(to.surface.z - from.surface.z) > threshold + 1e-6) continue;
      const fromCenter = footprintCenter(from.centerBounds);
      const toCenter = footprintCenter(to.centerBounds);
      const cost = Math.hypot(toCenter.x - fromCenter.x, toCenter.y - fromCenter.y)
        + Math.abs(to.surface.z - from.surface.z);
      if (distances[current]! + cost + 1e-9 < distances[next]!) {
        distances[next] = distances[current]! + cost;
        previous[next] = current;
      }
    }
  }

  let resolvedGoal = goalIndex ?? -1;
  if (goalIndex === undefined) {
    let best = Number.POSITIVE_INFINITY;
    for (let index = 0; index < nodes.length; index++) {
      const node = nodes[index]!;
      if (!Number.isFinite(distances[index])
        || Math.abs(node.surface.z - args.baseSupportZ) > threshold + 1e-6) continue;
      const center = footprintCenter(node.centerBounds);
      const total = distances[index]!
        + Math.hypot(center.x - args.destinationFt.x, center.y - args.destinationFt.y)
        + Math.abs(node.surface.z - args.baseSupportZ);
      if (total < best) { best = total; resolvedGoal = index; }
    }
  }
  if (resolvedGoal < 0 || !Number.isFinite(distances[resolvedGoal])) return null;
  const chain: Ft3dSupportSurface[] = [];
  for (let cursor = resolvedGoal; cursor >= 0; cursor = previous[cursor]!) {
    chain.push(nodes[cursor]!.surface);
    if (cursor === startIndex || previous[cursor] === -1) break;
  }
  chain.reverse();
  return chain;
}

export function ft3dSupportChainWaypoints(
  chain: Ft3dSupportSurface[],
  originSupportId: string | null,
  destinationSupportId: string | null,
  moverWidthFt: number,
  moverDepthFt: number,
): Array<[number, number]> {
  return chain.flatMap(surface => {
    if (surface.id === originSupportId || surface.id === destinationSupportId) return [];
    const bounds = insetFootprint(surface.footprint, moverWidthFt / 2, moverDepthFt / 2);
    if (!bounds) return [];
    const center = footprintCenter(bounds);
    return [[center.x, center.y] as [number, number]];
  });
}

export function ft3dElevationTransitionChainPath(
  routeFt: Array<[number, number]>,
  originFt: Ft3dPathPoint,
  destinationFt: Ft3dPathPoint,
  surfaces: Ft3dSupportSurface[],
  baseSupportZ: number,
  stepUpThresholdFt: number,
): Ft3dElevationTransition {
  if (routeFt.length < 2 || surfaces.length === 0) {
    return ft3dElevationTransitionPath(
      routeFt, originFt, destinationFt, surfaces[0]?.footprint ?? null, stepUpThresholdFt,
    );
  }
  const active = new Set<number>();
  surfaces.forEach((surface, index) => {
    if (insideFootprint([routeFt[0]![0], routeFt[0]![1]], surface.footprint)) active.add(index);
  });
  const result: Ft3dPathPoint[] = [{ x: routeFt[0]![0], y: routeFt[0]![1], z: originFt.z }];
  const links: Ft3dElevationLink[] = [];
  let currentZ = originFt.z;
  const desiredZ = (): number => {
    const indexes = [...active].sort((left, right) => right - left);
    return indexes.length > 0 ? surfaces[indexes[0]!]!.z : baseSupportZ;
  };
  for (let segmentIndex = 1; segmentIndex < routeFt.length; segmentIndex++) {
    const from = routeFt[segmentIndex - 1]!;
    const to = routeFt[segmentIndex]!;
    // Reconcile departing support at a shared boundary vertex. Internal events
    // intentionally skip t=0/t=1; membership must still change when the next
    // segment turns back off a top or enters one exactly at its first point.
    if (Math.hypot(to[0]-from[0],to[1]-from[1]) > EPS) {
      active.clear();
      surfaces.forEach((surface,index) => {
        const interval=footprintIntervalOnSegment(from,to,surface.footprint);
        if(interval && interval.enter<=1e-7 && interval.exit>1e-7) active.add(index);
      });
      const nextZ=desiredZ();
      if(Math.abs(nextZ-currentZ)>1e-6){
        const boundary={x:from[0],y:from[1]};
        const kind=Math.abs(nextZ-currentZ)<=Math.max(2.5,stepUpThresholdFt)+1e-6 ? 'step' as const : 'climb' as const;
        links.push({fromZ:currentZ,toZ:nextZ,boundaryFt:boundary,kind});
        result.push({...boundary,z:nextZ});currentZ=nextZ;
      }
    }
    const events: Array<{ t: number; surfaceIndex: number; enter: boolean }> = [];
    surfaces.forEach((surface, surfaceIndex) => {
      const interval = footprintIntervalOnSegment(from, to, surface.footprint);
      if (!interval) return;
      const startsInside = insideFootprint(from, surface.footprint);
      if (!startsInside && interval.enter > 1e-7 && interval.enter <= 1 + 1e-7) {
        events.push({ t: interval.enter, surfaceIndex, enter: true });
      }
      if (interval.exit > 1e-7 && interval.exit < 1 - 1e-7) {
        events.push({ t: interval.exit, surfaceIndex, enter: false });
      }
    });
    events.sort((left, right) => left.t - right.t || Number(right.enter) - Number(left.enter));
    for (let eventIndex = 0; eventIndex < events.length;) {
      const t = events[eventIndex]!.t;
      const group = [];
      while (eventIndex < events.length && Math.abs(events[eventIndex]!.t - t) <= 1e-7) {
        group.push(events[eventIndex++]!);
      }
      const boundary = { x: from[0] + (to[0] - from[0]) * t, y: from[1] + (to[1] - from[1]) * t };
      result.push({ ...boundary, z: currentZ });
      group.forEach(event => event.enter ? active.add(event.surfaceIndex) : active.delete(event.surfaceIndex));
      const nextZ = desiredZ();
      if (Math.abs(nextZ - currentZ) > 1e-6) {
        const kind = Math.abs(nextZ - currentZ) <= Math.max(2.5, stepUpThresholdFt) + 1e-6
          ? 'step' as const : 'climb' as const;
        links.push({ fromZ: currentZ, toZ: nextZ, boundaryFt: boundary, kind });
        result.push({ ...boundary, z: nextZ });
        currentZ = nextZ;
      }
    }
    result.push({ x: to[0], y: to[1], z: currentZ });
  }
  if (Math.abs(currentZ - destinationFt.z) > 1e-6) {
    const boundary = { x: destinationFt.x, y: destinationFt.y };
    const kind = Math.abs(destinationFt.z - currentZ) <= Math.max(2.5, stepUpThresholdFt) + 1e-6
      ? 'step' as const : 'climb' as const;
    links.push({ fromZ: currentZ, toZ: destinationFt.z, boundaryFt: boundary, kind });
    result.push({ ...destinationFt });
  }
  const kind: Ft3dElevationTransitionKind = links.some(link => link.kind === 'climb')
    ? 'climb' : links.length > 0 ? 'step' : 'none';
  return {
    pathFt: result,
    kind,
    riseFt: destinationFt.z - originFt.z,
    boundaryFt: links[0]?.boundaryFt ?? null,
    links,
  };
}

/**
 * Inserts the authoritative vertical face link where a routed floor path crosses
 * an object-top support footprint. The returned vertices deliberately retain the
 * exact boundary twice (old Z, new Z); the spatial resampler turns that edge into
 * real one-foot movement samples without smearing the elevation across the trip.
 */
export function ft3dElevationTransitionPath(
  routeFt: Array<[number, number]>,
  originFt: Ft3dPathPoint,
  destinationFt: Ft3dPathPoint,
  footprint: Footprint | null,
  stepUpThresholdFt: number,
): Ft3dElevationTransition {
  const riseFt = destinationFt.z - originFt.z;
  const kind: Ft3dElevationTransitionKind = Math.abs(riseFt) <= 1e-6
    ? 'none'
    : Math.abs(riseFt) <= Math.max(2.5, stepUpThresholdFt) + 1e-6
      ? 'step'
      : 'climb';
  if (routeFt.length < 2 || !footprint || kind === 'none') {
    return {
      pathFt: routeFt.length > 0
        ? routeFt.map((point, index) => ({
          x: point[0], y: point[1], z: index === routeFt.length - 1 ? destinationFt.z : originFt.z,
        }))
        : [{ ...originFt }, { ...destinationFt }],
      kind,
      riseFt,
      boundaryFt: null,
    };
  }

  const ascending = riseFt > 0;
  for (let index = 1; index < routeFt.length; index++) {
    const previous = routeFt[index - 1]!;
    const current = routeFt[index]!;
    const crosses = ascending
      ? !insideFootprint(previous, footprint) && insideFootprint(current, footprint)
      : insideFootprint(previous, footprint) && !insideFootprint(current, footprint);
    if (!crosses) continue;
    const boundary = footprintBoundaryOnSegment(previous, current, footprint);
    if (!boundary) continue;
    return {
      pathFt: [
        ...routeFt.slice(0, index).map(point => ({ x: point[0], y: point[1], z: originFt.z })),
        { x: boundary[0], y: boundary[1], z: originFt.z },
        { x: boundary[0], y: boundary[1], z: destinationFt.z },
        ...routeFt.slice(index).map(point => ({ x: point[0], y: point[1], z: destinationFt.z })),
      ],
      kind,
      riseFt,
      boundaryFt: { x: boundary[0], y: boundary[1] },
    };
  }

  return {
    pathFt: [
      ...routeFt.slice(0, -1).map(point => ({ x: point[0], y: point[1], z: originFt.z })),
      { ...destinationFt },
    ],
    kind,
    riseFt,
    boundaryFt: null,
  };
}

/** Arc-length resampling in true XYZ, retaining a single partial endpoint. */
export function resampleFt3dSpatialPath(points: Ft3dPathPoint[], stepFt = 1): Ft3dPathPoint[] {
  const step = Math.max(EPS, stepFt);
  const clean: Ft3dPathPoint[] = [];
  for (const point of points) {
    if (![point.x, point.y, point.z].every(Number.isFinite)) continue;
    const previous = clean.length > 0 ? clean[clean.length - 1] : undefined;
    if (previous && Math.hypot(
      point.x - previous.x, point.y - previous.y, point.z - previous.z,
    ) < EPS) continue;
    clean.push({ ...point });
  }
  if (clean.length <= 1) return clean;

  const output = [{ ...clean[0]! }];
  let carry = 0;
  for (let index = 1; index < clean.length; index++) {
    const from = clean[index - 1]!;
    const to = clean[index]!;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const length = Math.hypot(dx, dy, dz);
    if (length < EPS) continue;
    // Keep vertical-face corners: interpolation across a missing corner cuts through the support.
    const previous = clean[index - 2];
    if (previous && (Math.abs(previous.z - from.z) > EPS) !== (Math.abs(dz) > EPS)) {
      const last = output[output.length - 1]!;
      if (Math.hypot(last.x - from.x, last.y - from.y, last.z - from.z) > EPS) output.push({ ...from });
      carry = 0;
    }
    let travelled = 0;
    while (carry + (length - travelled) >= step - EPS) {
      travelled += step - carry;
      carry = 0;
      const ratio = travelled / length;
      output.push({
        x: from.x + dx * ratio,
        y: from.y + dy * ratio,
        z: from.z + dz * ratio,
      });
    }
    carry += length - travelled;
  }
  const end = clean[clean.length - 1]!;
  if (carry > EPS) output.push({ ...end });
  else output[output.length - 1] = { ...end };
  return output;
}

export function ft3dSpatialPathLength(points: Ft3dPathPoint[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index++) {
    const from = points[index - 1]!;
    const to = points[index]!;
    total += Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
  }
  return total;
}
